import React, { useMemo } from "react";
import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { getRouteOptions, normaliseUrl } from "../utilities/routeOptions";

const filter = createFilterOptions();

/**
 * Destination picker for a menu item. Offers the app's real routes as an
 * autocomplete so users select a valid page instead of hand-typing a path.
 * `freeSolo` still allows a custom value for edge cases (external/legacy links).
 */
export default function RoutePicker({
  value,
  onChange,
  label = "Links to",
  size = "small",
}) {
  const options = useMemo(() => getRouteOptions(), []);
  const current = value
    ? options.find((o) => o.value === normaliseUrl(value)) || {
        value: normaliseUrl(value),
        label: normaliseUrl(value),
      }
    : null;

  return (
    <Autocomplete
      size={size}
      freeSolo
      selectOnFocus
      handleHomeEndKeys
      options={options}
      value={current}
      onChange={(_e, next) => {
        if (next == null) return onChange("");
        if (typeof next === "string") return onChange(normaliseUrl(next));
        if (next.inputValue) return onChange(normaliseUrl(next.inputValue));
        return onChange(normaliseUrl(next.value));
      }}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const trimmed = params.inputValue.trim();
        if (trimmed !== "" && !opts.some((o) => o.value === normaliseUrl(trimmed))) {
          filtered.push({
            inputValue: trimmed,
            label: `Use custom: "${normaliseUrl(trimmed)}"`,
          });
        }
        return filtered;
      }}
      getOptionLabel={(opt) => {
        if (typeof opt === "string") return opt;
        if (opt.inputValue) return opt.inputValue;
        return opt.label || "";
      }}
      isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search pages"
          helperText="Pick a page — no leading slash needed"
        />
      )}
    />
  );
}
